import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { AdminLayout } from '../components'
import { Modal, ConfirmModal, Select, Input } from '../../../shared/components'
import { AdminFilterTrigger, AdminFilterSidebar } from '../components'
import adminShippingZoneService, { ShippingZone, ShippingZoneFormValues } from '../../../services/adminShippingZoneService'
import api from '../../../services/api'

const CSS_HANDLES = [
  'adminShippingZones',
  'zonesHeader',
  'zonesTitle',
  'zonesSubtitle',
  'zonesActions',
  'addZoneButton',
  'zonesTable',
  'tableHeader',
  'tableRow',
  'zoneInfo',
  'zoneName',
  'zoneStatus',
  'zoneActions',
  'editButton',
  'deleteButton',
  'statusToggle',
  'statusActive',
  'statusInactive',
  'modal',
  'modalOverlay',
  'modalContent',
  'modalHeader',
  'modalTitle',
  'modalClose',
  'modalBody',
  'formGroup',
  'formLabel',
  'formInput',
  'formSelect',
  'formRow',
  'modalFooter',
  'cancelButton',
  'saveButton',
  'pagination',
  'paginationButton',
  'paginationInfo'
] as const

interface ShippingMethod {
  id: string
  name: string
}

const AdminShippingZones: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const [zones, setZones] = useState<ShippingZone[]>([])
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingZone, setEditingZone] = useState<ShippingZone | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState<{ page: number; limit: number; total: number; totalPages: number } | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false)
  const [pendingDeletion, setPendingDeletion] = useState<string | null>(null)
  const [pendingStatusToggle, setPendingStatusToggle] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [zoneToDelete, setZoneToDelete] = useState<ShippingZone | null>(null)

  type ShippingZoneFormData = Omit<ShippingZoneFormValues, 'isActive'> & {
    isActive: string
  }

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<ShippingZoneFormData>({
    defaultValues: {
      name: '',
      zipCodeStart: '',
      zipCodeEnd: '',
      shippingMethodId: '',
      price: 0,
      estimatedDays: 1,
      isActive: 'true'
    }
  })

  const formatZipCode = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{3})\d+?$/, '$1')
  }

  const defaultFilters = { search: '', isActive: undefined as boolean | undefined, shippingMethodId: '' }

  const [filters, setFilters] = useState<{ search: string; isActive?: boolean; shippingMethodId?: string }>(() => ({
    ...defaultFilters
  }))
  const [draftFilters, setDraftFilters] = useState<{ search: string; isActive?: boolean; shippingMethodId?: string }>(() => ({
    ...defaultFilters
  }))

  const itemsPerPage = 20

  const resolveErrorMessage = (err: any, defaultMessage: string): string => {
    if (err?.message) return err.message
    if (err?.response?.data?.message) return err.response.data.message
    if (typeof err?.response?.data === 'string') return err.response.data
    return defaultMessage
  }

  const previousFiltersRef = useRef<string | null>(null)
  const previousPageRef = useRef<number>(1)
  const isLoadingRef = useRef(false)

  useEffect(() => {
    const fetchShippingMethods = async () => {
      try {
        const response = await api.get<{ success: boolean; data: { methods: ShippingMethod[] } }>('/shipping/methods')
        setShippingMethods(response.data.data.methods)
      } catch (err) {
        console.error('Erro ao buscar métodos de envio:', err)
      }
    }
    fetchShippingMethods()
  }, [])

  const fetchZones = useCallback(async (page: number) => {
    const filtersKey = JSON.stringify({ 
      search: filters.search.trim(), 
      isActive: filters.isActive,
      shippingMethodId: filters.shippingMethodId
    })
    
    const filtersChanged = previousFiltersRef.current !== filtersKey
    const pageChanged = previousPageRef.current !== page

    if (!filtersChanged && !pageChanged && isLoadingRef.current) {
      return
    }

    if (!filtersChanged && !pageChanged && !isLoadingRef.current && previousFiltersRef.current === filtersKey) {
      return
    }

    isLoadingRef.current = true
    previousFiltersRef.current = filtersKey
    previousPageRef.current = page

    setIsFetching(true)
    setError(null)

    try {
      const response = await adminShippingZoneService.list({
        page,
        limit: itemsPerPage,
        search: filters.search.trim() || undefined,
        isActive: filters.isActive,
        shippingMethodId: filters.shippingMethodId || undefined
      })

      setZones(response.zones)
      setPagination(response.pagination)
      setCurrentPage(response.pagination.page)
      setHasLoaded(true)
    } catch (err) {
      console.error('Erro ao buscar zonas de entrega:', err)
      setError(resolveErrorMessage(err, 'Não foi possível carregar as zonas de entrega.'))
      setHasLoaded(true)
    } finally {
      setIsFetching(false)
      isLoadingRef.current = false
    }
  }, [filters, itemsPerPage])

  useEffect(() => {
    fetchZones(1)
  }, [fetchZones])

  const hasActiveFilters = filters.search.trim() !== '' || filters.isActive !== undefined || (filters.shippingMethodId !== undefined && filters.shippingMethodId !== '')

  const handleOpenFilters = () => {
    setDraftFilters({ ...filters })
    setIsFilterSidebarOpen(true)
  }

  const handleCloseFilters = () => {
    setIsFilterSidebarOpen(false)
  }

  const handleDraftFilterChange = <Key extends keyof typeof draftFilters>(key: Key, value: (typeof draftFilters)[Key]) => {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const handleApplyFilters = () => {
    setFilters({ ...draftFilters })
    setCurrentPage(1)
    setIsFilterSidebarOpen(false)
  }

  const handleClearFilters = () => {
    const reset = { ...defaultFilters }
    setDraftFilters(reset)
    setFilters(reset)
    setCurrentPage(1)
  }

  const handleAddZone = () => {
    setActionError(null)
    setEditingZone(null)
    setIsModalOpen(true)
  }

  const handleEditZone = (zone: ShippingZone) => {
    setActionError(null)
    setEditingZone(zone)
    setIsModalOpen(true)
  }

  const handleDeleteZone = (zone: ShippingZone) => {
    setZoneToDelete(zone)
    setDeleteConfirmOpen(true)
  }

  const confirmDeleteZone = async () => {
    if (!zoneToDelete) return

    setPendingDeletion(zoneToDelete.id)
    setActionError(null)
    setDeleteConfirmOpen(false)

    try {
      await adminShippingZoneService.remove(zoneToDelete.id)

      const isLastItemOnPage = zones.length === 1 && currentPage > 1
      const nextPage = isLastItemOnPage ? currentPage - 1 : currentPage

      if (isLastItemOnPage) {
        setCurrentPage(nextPage)
      }

      // Resetar refs para forçar atualização
      previousFiltersRef.current = null
      previousPageRef.current = 0
      await fetchZones(nextPage)
    } catch (err) {
      console.error('Erro ao excluir zona de entrega:', err)
      setActionError(
        resolveErrorMessage(err, 'Não foi possível excluir a zona de entrega. Tente novamente.')
      )
    } finally {
      setPendingDeletion(null)
      setZoneToDelete(null)
    }
  }

  const handleToggleStatus = async (zoneId: string, currentStatus: boolean) => {
    setPendingStatusToggle(zoneId)
    setActionError(null)

    try {
      await adminShippingZoneService.updateStatus(zoneId, !currentStatus)
      await fetchZones(currentPage)
    } catch (err) {
      console.error('Erro ao atualizar status da zona de entrega:', err)
      setActionError(
        resolveErrorMessage(err, 'Não foi possível atualizar o status da zona de entrega.')
      )
    } finally {
      setPendingStatusToggle(null)
    }
  }

  const onSubmitZone = async (data: ShippingZoneFormData) => {
    setIsSaving(true)
    setActionError(null)

    const formValues: ShippingZoneFormValues = {
      name: data.name,
      zipCodeStart: data.zipCodeStart.replace(/\D/g, ''),
      zipCodeEnd: data.zipCodeEnd.replace(/\D/g, ''),
      shippingMethodId: data.shippingMethodId,
      price: data.price,
      estimatedDays: data.estimatedDays,
      isActive: data.isActive === 'true'
    }

    try {
      if (editingZone) {
        await adminShippingZoneService.update(editingZone.id, formValues)
        // Resetar refs para forçar atualização
        previousFiltersRef.current = null
        previousPageRef.current = 0
        const pageToFetch = pagination?.page ?? currentPage
        await fetchZones(pageToFetch)
      } else {
        await adminShippingZoneService.create(formValues)
        setCurrentPage(1)
        // Resetar refs para forçar atualização
        previousFiltersRef.current = null
        previousPageRef.current = 0
        await fetchZones(1)
      }

      setIsModalOpen(false)
      setEditingZone(null)
      reset({
        name: '',
        zipCodeStart: '',
        zipCodeEnd: '',
        shippingMethodId: '',
        price: 0,
        estimatedDays: 1,
        isActive: 'true'
      })
    } catch (err) {
      console.error('Erro ao salvar zona de entrega:', err)
      const message = resolveErrorMessage(
        err,
        'Não foi possível salvar a zona de entrega. Verifique os dados e tente novamente.'
      )
      setActionError(message)
      throw err
    } finally {
      setIsSaving(false)
    }
  }

  // Reset form when editing zone changes
  useEffect(() => {
    if (editingZone) {
      reset({
        name: editingZone.name || '',
        zipCodeStart: editingZone.zipCodeStart || '',
        zipCodeEnd: editingZone.zipCodeEnd || '',
        shippingMethodId: editingZone.shippingMethodId || '',
        price: editingZone.price || 0,
        estimatedDays: editingZone.estimatedDays || 1,
        isActive: editingZone.isActive ? 'true' : 'false'
      })
    } else {
      reset({
        name: '',
        zipCodeStart: '',
        zipCodeEnd: '',
        shippingMethodId: '',
        price: 0,
        estimatedDays: 1,
        isActive: 'true'
      })
    }
  }, [editingZone, reset])

  const isApplyDisabled =
    draftFilters.search === filters.search && 
    draftFilters.isActive === filters.isActive &&
    draftFilters.shippingMethodId === filters.shippingMethodId

  const totalPages = pagination?.totalPages ?? 1
  const totalItems = pagination?.total ?? zones.length
  const currentPageNumber = pagination?.page ?? currentPage
  const limit = pagination?.limit ?? itemsPerPage
  const startIndex = totalItems === 0 ? 0 : (currentPageNumber - 1) * limit
  const endIndex = totalItems === 0 ? 0 : Math.min(startIndex + zones.length, totalItems)
  const showLoadingOverlay = hasLoaded && isFetching
  const activeFiltersDescriptions = [
    filters.isActive !== undefined ? `status: ${filters.isActive ? 'ativos' : 'inativos'}` : null,
    filters.search.trim() ? `busca: "${filters.search.trim()}"` : null,
    filters.shippingMethodId ? `método: ${shippingMethods.find(m => m.id === filters.shippingMethodId)?.name || filters.shippingMethodId}` : null
  ]
    .filter(Boolean)
    .join(' • ')

  const renderSkeleton = () => (
    <AdminLayout>
      <div className="space-y-8">
        <div className="space-y-3">
          <div className="h-8 w-64 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-80 animate-pulse rounded bg-gray-200" />
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-80" />
            <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          </div>
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-4 h-6 w-48 animate-pulse rounded bg-gray-200" />
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`skeleton-row-${index}`} className="h-16 animate-pulse rounded bg-gray-100" />
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  )

  if (!hasLoaded && isFetching) {
    return renderSkeleton()
  }

  if (error && !hasLoaded && !isFetching) {
    return (
      <AdminLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="space-y-4 text-center">
            <p className="text-gray-600">{error}</p>
            <button
              onClick={() => fetchZones(1)}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="relative">
        {showLoadingOverlay && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/60">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          </div>
        )}

        <div className={`${handles.adminShippingZones} min-h-screen bg-gray-50`}>
          <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
            <div className={`${handles.zonesHeader}`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className={`${handles.zonesTitle} text-3xl font-bold text-gray-900 mb-2`}>
                    Zonas de Entrega
                  </h1>
                  <p className={`${handles.zonesSubtitle} text-lg text-gray-600`}>
                    Gerencie as zonas de entrega do sistema
                  </p>
                </div>
                <button
                  onClick={handleAddZone}
                  className={`${handles.addZoneButton} inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
                >
                  Adicionar Zona
                </button>
              </div>
            </div>

            {actionError && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                {actionError}
              </div>
            )}

            {error && hasLoaded && !isFetching && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <span>{error}</span>
                  <button
                    onClick={() => fetchZones(currentPageNumber)}
                    className="inline-flex items-center text-red-700 underline hover:text-red-800"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            )}

            <div className={`${handles.zonesActions} rounded-xl bg-white p-6 shadow-sm`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    Exibindo{' '}
                    <span className="font-semibold text-gray-900">
                      {totalItems === 0 ? 0 : startIndex + 1} - {endIndex}
                    </span>{' '}
                    de{' '}
                    <span className="font-semibold text-gray-900">{totalItems}</span>
                    {' '}zonas de entrega
                  </p>
                  {hasActiveFilters && (
                    <p className="mt-1 text-xs text-blue-600">
                      Filtros aplicados: {activeFiltersDescriptions}
                    </p>
                  )}
                </div>
                <AdminFilterTrigger
                  onClick={handleOpenFilters}
                  hasActiveFilters={hasActiveFilters}
                />
              </div>
            </div>

            <div className="rounded-lg bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className={`${handles.zonesTable} w-full`}>
                  <thead className={`${handles.tableHeader} bg-gray-50`}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        CEP Inicial
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        CEP Final
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Método de Envio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Preço
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Prazo (dias)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                      <th className="sticky right-0 z-20 bg-gray-50 px-2 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 shadow-[inset_1px_0_0_0_rgb(226,232,240)] w-24">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {zones.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                          Nenhuma zona de entrega encontrada para os filtros selecionados.
                        </td>
                      </tr>
                    ) : (
                      zones.map((zone) => (
                        <tr key={zone.id} className={`${handles.tableRow} hover:bg-gray-50`}>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className={`${handles.zoneInfo}`}>
                              <div className={`${handles.zoneName} text-sm font-medium text-gray-900`}>
                                {zone.name}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            {zone.zipCodeStart}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            {zone.zipCodeEnd}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            {zone.shippingMethod?.name || '-'}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            R$ {Number(zone.price).toFixed(2).replace('.', ',')}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            {zone.estimatedDays}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <button
                              onClick={() => handleToggleStatus(zone.id, zone.isActive)}
                              disabled={pendingStatusToggle === zone.id}
                              className={`${handles.statusToggle} ${
                                zone.isActive
                                  ? `${handles.statusActive} bg-green-100 text-green-800`
                                  : `${handles.statusInactive} bg-red-100 text-red-800`
                              } rounded-full px-3 py-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                                zone.isActive ? 'focus-visible:ring-green-300' : 'focus-visible:ring-red-300'
                              } disabled:cursor-not-allowed disabled:opacity-60`}
                            >
                              {pendingStatusToggle === zone.id
                                ? 'Atualizando...'
                                : zone.isActive
                                ? 'Ativo'
                                : 'Inativo'}
                            </button>
                          </td>
                          <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-2 py-4 text-sm font-medium shadow-[inset_1px_0_0_0_rgb(226,232,240)] w-24">
                            <div className={`${handles.zoneActions} flex space-x-2 justify-center`}>
                              <button
                                onClick={() => handleEditZone(zone)}
                                title="Editar zona"
                                aria-label="Editar zona"
                                className={`${handles.editButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-yellow-400 text-gray-900 shadow-sm transition-colors hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2`}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16.862 3.487a2.1 2.1 0 112.971 2.97L7.5 18.79 3 21l2.21-4.5 11.652-12.013z" />
                                </svg>
                                <span className="sr-only">Editar zona</span>
                              </button>
                              <button
                                onClick={() => void handleDeleteZone(zone)}
                                title="Excluir zona"
                                aria-label="Excluir zona"
                                disabled={pendingDeletion === zone.id}
                                aria-busy={pendingDeletion === zone.id}
                                className={`${handles.deleteButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-yellow-400 text-gray-900 shadow-sm transition-colors hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60`}
                              >
                                {pendingDeletion === zone.id ? (
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-700 border-t-transparent" aria-hidden="true" />
                                ) : (
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-4 0h14" />
                                  </svg>
                                )}
                                <span className="sr-only">
                                  {pendingDeletion === zone.id ? 'Excluindo zona' : 'Excluir zona'}
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className={`${handles.pagination} bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200`}>
                  <div className={`${handles.paginationInfo} text-sm text-gray-700`}>
                    {totalItems > 0
                      ? `Mostrando ${startIndex + 1} a ${endIndex} de ${totalItems} zonas de entrega`
                      : 'Nenhuma zona de entrega encontrada'}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPageNumber === 1}
                      className={`${handles.paginationButton} px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50`}
                    >
                      Anterior
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`${handles.paginationButton} px-3 py-1 border rounded-md ${
                          page === currentPageNumber
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPageNumber === totalPages}
                      className={`${handles.paginationButton} px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50`}
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AdminFilterSidebar
        isOpen={isFilterSidebarOpen}
        onClose={handleCloseFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        isApplyDisabled={isApplyDisabled}
        applyLabel="Aplicar filtros"
        clearLabel="Limpar filtros"
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor="zone-filter-search"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Buscar zonas
            </label>
            <Input
              id="zone-filter-search"
              type="text"
              value={draftFilters.search}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleDraftFilterChange('search', event.target.value)
              }
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
              placeholder="Nome, CEP inicial ou CEP final"
            />
          </div>

          <div>
            <label
              htmlFor="zone-filter-method"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Método de Envio
            </label>
            <Select
              id="zone-filter-method"
              name="zone-filter-method"
              value={draftFilters.shippingMethodId || ''}
              onChange={(value) =>
                handleDraftFilterChange('shippingMethodId', value || undefined)
              }
              options={[
                { value: '', label: 'Todos os métodos' },
                ...shippingMethods.map((method) => ({
                  value: method.id,
                  label: method.name
                }))
              ]}
              placeholder="Todos os métodos"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label
              htmlFor="zone-filter-status"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Status
            </label>
            <Select
              id="zone-filter-status"
              name="zone-filter-status"
              value={draftFilters.isActive === undefined ? 'all' : draftFilters.isActive ? 'active' : 'inactive'}
              onChange={(value) =>
                handleDraftFilterChange(
                  'isActive',
                  value === 'all' ? undefined : value === 'active'
                )
              }
              options={[
                { value: 'all', label: 'Todos os status' },
                { value: 'active', label: 'Ativos' },
                { value: 'inactive', label: 'Inativos' }
              ]}
              placeholder="Todos os status"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </AdminFilterSidebar>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEditingZone(null)
          reset({
            name: '',
            zipCodeStart: '',
            zipCodeEnd: '',
            shippingMethodId: '',
            price: 0,
            estimatedDays: 1,
            isActive: 'true'
          })
        }}
        title={editingZone ? 'Editar Zona de Entrega' : 'Nova Zona de Entrega'}
      >
        <form onSubmit={handleSubmit(onSubmitZone)} className="space-y-4">
          {actionError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {actionError}
            </div>
          )}

          <div className={`${handles.formGroup}`}>
            <label htmlFor="name" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
              Nome *
            </label>
            <Input
              type="text"
              id="name"
              {...register('name', {
                required: 'Nome é obrigatório'
              })}
              className={`${handles.formInput} mt-1 ${errors.name ? 'border-red-300' : ''}`}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          </div>

          <div className={`${handles.formRow} grid grid-cols-2 gap-4`}>
            <div className={`${handles.formGroup}`}>
              <label htmlFor="zipCodeStart" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
                CEP Inicial *
              </label>
              <Controller
                name="zipCodeStart"
                control={control}
                rules={{
                  required: 'CEP inicial é obrigatório',
                  pattern: {
                    value: /^\d{5}-?\d{3}$/,
                    message: 'CEP inválido (formato: 00000-000)'
                  }
                }}
                render={({ field }) => (
                  <Input
                    type="text"
                    id="zipCodeStart"
                    value={field.value}
                    onChange={(e) => {
                      const formatted = formatZipCode(e.target.value)
                      field.onChange(formatted)
                    }}
                    placeholder="00000-000"
                    maxLength={9}
                    className={`${handles.formInput} mt-1 ${errors.zipCodeStart ? 'border-red-300' : ''}`}
                    error={!!errors.zipCodeStart}
                    helperText={errors.zipCodeStart?.message}
                  />
                )}
              />
            </div>

            <div className={`${handles.formGroup}`}>
              <label htmlFor="zipCodeEnd" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
                CEP Final *
              </label>
              <Controller
                name="zipCodeEnd"
                control={control}
                rules={{
                  required: 'CEP final é obrigatório',
                  pattern: {
                    value: /^\d{5}-?\d{3}$/,
                    message: 'CEP inválido (formato: 00000-000)'
                  }
                }}
                render={({ field }) => (
                  <Input
                    type="text"
                    id="zipCodeEnd"
                    value={field.value}
                    onChange={(e) => {
                      const formatted = formatZipCode(e.target.value)
                      field.onChange(formatted)
                    }}
                    placeholder="00000-000"
                    maxLength={9}
                    className={`${handles.formInput} mt-1 ${errors.zipCodeEnd ? 'border-red-300' : ''}`}
                    error={!!errors.zipCodeEnd}
                    helperText={errors.zipCodeEnd?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className={`${handles.formGroup}`}>
            <label htmlFor="shippingMethodId" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
              Método de Envio *
            </label>
            <Controller
              name="shippingMethodId"
              control={control}
              rules={{
                required: 'Método de envio é obrigatório'
              }}
              render={({ field }) => (
                <>
                  <Select
                    id="shippingMethodId"
                    value={field.value}
                    onChange={field.onChange}
                    options={shippingMethods.map(method => ({
                      value: method.id,
                      label: method.name
                    }))}
                    placeholder="Selecione o método de envio"
                    className={`${handles.formSelect} mt-1`}
                  />
                  {errors.shippingMethodId && (
                    <p className="mt-1 text-sm text-red-600">{errors.shippingMethodId.message}</p>
                  )}
                </>
              )}
            />
          </div>

          <div className={`${handles.formRow} grid grid-cols-2 gap-4`}>
            <div className={`${handles.formGroup}`}>
              <label htmlFor="price" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
                Preço (R$) *
              </label>
              <Input
                type="number"
                id="price"
                {...register('price', {
                  required: 'Preço é obrigatório',
                  valueAsNumber: true,
                  min: { value: 0, message: 'Preço deve ser maior ou igual a 0' }
                })}
                step="0.01"
                className={`${handles.formInput} mt-1 ${errors.price ? 'border-red-300' : ''}`}
                error={!!errors.price}
                helperText={errors.price?.message}
              />
            </div>

            <div className={`${handles.formGroup}`}>
              <label htmlFor="estimatedDays" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
                Prazo (dias) *
              </label>
              <Input
                type="number"
                id="estimatedDays"
                {...register('estimatedDays', {
                  required: 'Prazo é obrigatório',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Prazo deve ser maior ou igual a 1' }
                })}
                className={`${handles.formInput} mt-1 ${errors.estimatedDays ? 'border-red-300' : ''}`}
                error={!!errors.estimatedDays}
                helperText={errors.estimatedDays?.message}
              />
              {errors.estimatedDays && (
                <p className="mt-1 text-sm text-red-600">{errors.estimatedDays.message}</p>
              )}
            </div>
          </div>

          <div className={`${handles.formGroup}`}>
            <label htmlFor="isActive" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
              Status
            </label>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Select
                  id="isActive"
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    { value: 'true', label: 'Ativo' },
                    { value: 'false', label: 'Inativo' }
                  ]}
                  placeholder="Selecione o status"
                  className={`${handles.formSelect} mt-1`}
                />
              )}
            />
          </div>

          <div className={`${handles.modalFooter} flex justify-end gap-3 pt-4`}>
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false)
                setEditingZone(null)
                reset({
                  name: '',
                  zipCodeStart: '',
                  zipCodeEnd: '',
                  shippingMethodId: '',
                  price: 0,
                  estimatedDays: 1,
                  isActive: 'true'
                })
              }}
              className={`${handles.cancelButton} rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`${handles.saveButton} rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50`}
            >
              {isSaving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setZoneToDelete(null)
        }}
        onConfirm={confirmDeleteZone}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir a zona de entrega "${zoneToDelete?.name}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        confirmButtonClassName="bg-red-600 hover:bg-red-700"
        isLoading={pendingDeletion !== null}
      />
    </AdminLayout>
  )
}

export default AdminShippingZones

